import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead } from '../hooks';
import { NOTIFICATION_META, type Notification } from '../types';

/** Simple relative time formatter */
function timeAgo(dateStr: string): string {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
}

export default function NotificationPanel() {
    const [open, setOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const { data: unreadCount = 0 } = useUnreadCount();
    const { data: notifData, isLoading } = useNotifications(1);
    const markRead = useMarkRead();
    const markAllRead = useMarkAllRead();

    const notifications: Notification[] = notifData?.data ?? [];

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        function handleEscape(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }
        if (open) document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [open]);

    function handleNotificationClick(notification: Notification) {
        // Mark as read
        if (!notification.is_read) {
            markRead.mutate(notification.id);
        }
        // Navigate to link
        if (notification.link) {
            navigate(notification.link);
        }
        setOpen(false);
    }

    function handleMarkAllRead() {
        markAllRead.mutate();
    }

    return (
        <div className="relative" ref={panelRef}>
            {/* Bell Button */}
            <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setOpen(!open)}
                id="notification-bell"
            >
                <Bell className="h-5 w-5 text-gray-500" />
                {unreadCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </Button>

            {/* Dropdown Panel */}
            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-96 max-h-[480px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl z-50"
                    style={{
                        animation: 'slideDown 0.2s ease-out',
                    }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                        <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                            >
                                <CheckCheck className="h-3.5 w-3.5" />
                                Mark all as read
                            </button>
                        )}
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto max-h-[400px]">
                        {isLoading ? (
                            <div className="space-y-3 p-4">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="flex gap-3 animate-pulse">
                                        <div className="h-9 w-9 rounded-lg bg-gray-100" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-3 w-2/3 rounded bg-gray-100" />
                                            <div className="h-3 w-full rounded bg-gray-100" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="text-3xl mb-2">🎉</div>
                                <p className="text-sm font-medium text-gray-900">You're all caught up!</p>
                                <p className="text-xs text-gray-500 mt-1">No new notifications</p>
                            </div>
                        ) : (
                            notifications.map((notification) => {
                                const meta = NOTIFICATION_META[notification.type] ?? {
                                    icon: '🔔',
                                    color: '#6B7280',
                                };

                                return (
                                    <button
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`
                                            w-full flex items-start gap-3 px-4 py-3 text-left
                                            transition-colors hover:bg-gray-50 border-b border-gray-50
                                            ${!notification.is_read ? 'bg-indigo-50/50' : ''}
                                        `}
                                    >
                                        {/* Icon */}
                                        <div
                                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-lg"
                                            style={{ backgroundColor: `${meta.color}15` }}
                                        >
                                            {meta.icon}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm leading-tight ${!notification.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                                                    {notification.title}
                                                </p>
                                                {!notification.is_read && (
                                                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-500" />
                                                )}
                                            </div>
                                            <p className="mt-0.5 text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                                {notification.message}
                                            </p>
                                            <p className="mt-1 text-[11px] text-gray-400">
                                                {timeAgo(notification.created_at)}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="border-t border-gray-100 px-4 py-2.5">
                            <button
                                onClick={() => { setOpen(false); }}
                                className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Animation keyframe (injected once) */}
            <style>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-8px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
}
